// @flow
import {fromJS, List, Map} from 'immutable';
import {isFunction} from 'lodash';
import {Store} from 'client/framework';
import wizardActions from './Wizard.actions';

type wizardStep = Map<string, Function>;

export class WizardStore extends Store {
  constructor(name: string) {
    super(name);

    this.initialData = fromJS({
      activeStep: null,
      activeStepNumber: 0,
      fullStepList: [],
      movementHandler: null
    });

    this.setInitialData(this.initialData);
    this.handle(wizardActions.ADVANCE_STEP, this._makeAdvanceStep());
    this.handle(wizardActions.SET_INITIAL_STEP, this._setInitialStep);
    this.handle(wizardActions.SET_ACTIVE_STEP, this._setActiveStep);
    this.handle(wizardActions.SET_FULL_STEP_LIST, this._setFullStepList);
    this.handle(wizardActions.CLEAR_MOVEMENT_HANDLER, this._clearMovementHandler);
    this.handle(wizardActions.RETREAT_STEP, this._makeRetreatStep());
    this.handle(wizardActions.RESET_STEPS, this._resetSteps);
  }

  _clearMovementHandler() {
    this.writeData('movementHandler', this.initialData.get('movementHandler'));
  }

  _setInitialStep(initialStep: wizardStep) {
    this.writeData((state) => {
      return state
        .set('activeStep', initialStep.get('step'))
        .set('activeStepNumber', initialStep.get('stepNumber'));
    });
  }

  _stepMove(stepScaler: Function) {
    const currentStepNumber = this.getActiveStepNumber();
    const stepList = this._getFullStepList();

    const nextStepNumber = stepScaler(currentStepNumber);
    const nextStep = stepList.get(nextStepNumber);
    if (!nextStep) {
      throw new Error('Unable to change step. Out of bounds');
    }

    this._setActiveStep(
      new Map({
        step: nextStep,
        stepNumber: nextStepNumber
      })
    );
  }

  _makeAdvanceStep(): Function {
    return () => this._stepMove((stepNumber) => stepNumber + 1);
  }

  _makeRetreatStep(): Function {
    return () => this._stepMove((stepNumber) => stepNumber - 1);
  }

  _resetSteps(newSteps: List<wizardStep>) {
    const steps = newSteps || this._getFullStepList();
    this._setInitialStep(
      Map({
        step: steps.first(),
        stepNumber: 0
      })
    );

    this._setFullStepList(steps);
  }

  _setFullStepList(stepList: List<wizardStep>) {
    this.writeData('fullStepList', stepList);
  }

  _setActiveStep(incomingStep: Map<string, *>) {
    const activeStep = this.getActiveStep();
    const activeStepNumber = this.getActiveStepNumber();

    // Determine what direction the user is moving in.
    const isRetreating = incomingStep.get('stepNumber') < activeStepNumber;

    // placeholder for an advance or retreat handler.
    let handlerToWrite;

    // Determine if we are advancing or retreating:
    // Set the movementHandler where appropriate.
    if (isRetreating) {
      const canRetreatFunction = activeStep.get('canRetreat');
      let canRetreat = true;
      if (isFunction(canRetreatFunction)) {
        canRetreat = canRetreatFunction();
      }

      if (!canRetreat) {
        return;
      }

      // determine if opptional retreat has been provided.
      const onRetreatFunction = activeStep.get('onRetreat');
      if (isFunction(onRetreatFunction)) {
        handlerToWrite = onRetreatFunction;
      }
    } else {
      /*
        Determine if a user should be stuck on the active tab until a condition is met.

        This is an optionally provided field
      */
      const canAdvanceFunction = activeStep.get('canAdvance');
      let canAdvance = true;
      if (isFunction(canAdvanceFunction)) {
        canAdvance = canAdvanceFunction();
      }

      // Do nothing if the active step is re-selected, or the canAdvance predicate returned false
      if (!canAdvance) {
        return;
      }

      // Determine if our optional onAdvance has been provided.
      const onAdvanceFunction = activeStep.get('onAdvance');
      if (isFunction(onAdvanceFunction)) {
        handlerToWrite = onAdvanceFunction;
      }
    }

    this.writeData((state) => {
      return state
        .set('activeStep', incomingStep.get('step'))
        .set('activeStepNumber', incomingStep.get('stepNumber'))
        .set('movementHandler', handlerToWrite);
    });
  }

  getActiveStep(): wizardStep {
    return this.readData('activeStep');
  }

  getActiveStepNumber(): number {
    return this.readData('activeStepNumber');
  }

  _getFullStepList(): List<wizardStep> {
    return this.readData('fullStepList');
  }

  getMovementHandler(): Function {
    return this.readData('movementHandler');
  }
}

export default new WizardStore('WizardStore');
